import {
    ConnectionController,
    ConnectionCallBackEvent,
    initLogAndManager,
    Command,
    MessagePriority,
    DOCUMENT_TYPE,
    ConnectMode,
    RoomWebsocketErrorCode,
    RoomErrorCode,
} from '@tencent/docs-connection';
import { SocketEventTypes, generateShortUUID } from '@tencent/slide-sdk-model';
import { GetSlideAppErrorByCode, SlideAppError } from '@tencent/slide-sdk-revision';
import { network } from '@tencent/netcheck';
import ua from '@tencent/docs-user-agent';
import { isCorpWeChat } from './services/utils';
import { ServerMsgType } from './constants';
import type { ILogger } from '@tencent/slide-sdk-model/src/manager/logger.interface';
import type { IEventManager } from '@tencent/slide-sdk-model/src/manager/event-manager';
import type {
    IConnectionControllerOptions,
    IRoomCloseReason,
    IConnectionCallBackResult,
    IConnectionCallBackServerMsg,
    IRoomSendMessagePacket,
    IServerMessage,
    MessageDataType,
    IServerMessageBodyNoticeCursor,
    IServerMessageBodyNoticeTitle,
    IRoomSendMessageResult,
    IServerMessageBodyNoticeUserNewInfo,
    IServerMessageBodyNoticeUserLeave,
    IServerMessageBodyNoticeBase,
} from '@tencent/docs-connection';

// @tencent/docs-connection 没有完善该类型
export interface IServerMessageBodyNoticePresentInfo {
    /** globalPadId */
    docid: string;
    /** 业务场景，比如 meeting 腾讯会议 */
    biztype: any;
    /** 业务唯一 id */
    bizid: string;
    slideId: string;
    /**
     * 主讲人头像;
     * 主讲人信息原本规划应该在 api.getPresentInfo 的 `hostInfo` 字段中
     */
    avatar: string;
}

export interface ISocket {
    connectionInstance: ConnectionController;
    updateBaseRevision(rev: number): void;
    close(reason: string, errCode?: number): void;
    sendMessage(messageParams: IRoomSendMessagePacket): void;
    cancelMessageByTaskId(taskId: number): void;
    switchBigCollabMode(): void;
}

const MODULE_NAME = '[SDK][Soc]';
const SOCKET_ESTABLISH_TIME_OUT = 90000;
enum MonitorStatus {
    SUCCESS = 0,
    ERROR = 1,
    TIMEOUT = 2,
}

export class Socket implements ISocket {
    private connectionController: ConnectionController;

    /** 是否处于续票重连流程中 */
    private inRenewTicketFlow = false;
    private estMoreThan90 = true;
    private reportEstTimer: NodeJS.Timeout;

    /** IOS 强制使用 ws，其他端使用 ws 鉴权，后续切换到 chunk */
    private connectMode = ua.isIOS || ua.isSafari ? ConnectMode.WEBSOCKET_FORCED : ConnectMode.WEBSOCKET;
    private socketConfig: ICreateControllerParams;

    constructor(
        private clientVarProvider: {
            isOverConNum?: boolean;
            isCreator?: boolean;
            isDriveFile?: boolean;
        },
        private messageHandler: ISocketMessageHandler,
        private event: IEventManager,
        private logger: ILogger,
    ) {
        void this.event;
    }

    public get connectionInstance() {
        return this.connectionController;
    }

    /**
     * 是否处于连接状态
     */
    public get opened(): boolean {
        return this.connectionController?.isConnected();
    }

    public get isShutDown(): boolean {
        return this.connectionController?.isDisconnected();
    }

    public updateBaseRevision(rev: number) {
        this.connectionController?.setVersion(rev);
    }

    /**
     * 打开socket
     */
    public open() {
        this.monitor4OpenSocketTimeout();
        this.connectionController?.connect(this.connectMode);

        window.addEventListener('unload', () => {
            this.logger.info(MODULE_NAME, 'unload');
            const reason: IRoomCloseReason = {
                reason: 'unload',
                shutDown: true,
            };
            this.connectionController?.disconnect(reason);
        });
    }

    /**
     * 关闭 socket
     * @param reason
     * @param errCode
     */
    public close(reason: string, errCode?: number) {
        const closeReason: IRoomCloseReason = {
            reason,
            errCode,
            shutDown: true,
        };
        this.connectionController?.disconnect(closeReason);
    }

    public reConnect() {
        this.logger.info(MODULE_NAME, 'reConnect');
        this.connectionController?.reconnect();
    }

    /**
     * 发送消息
     * @param cmd 信令
     * @param taskId 序列
     * @param data 数据
     * @param customParam 参数
     * @param onError 发送失败回调(即原来返回的result)
     * @param onsend 发送成功回调(collabroom包中s是小写这里保持一致)
     * @param onsendAccept 发送ack回调(服务器接收到change的处理)
     */
    public sendMessage(messageParams: IRoomSendMessagePacket) {
        const message: IRoomSendMessagePacket = {
            ...messageParams,
            onsend:
                messageParams.onsend &&
                ((seq: number, cmd: string, data: MessageDataType, result: IRoomSendMessageResult) => {
                    this.reportWebsocketOnSend(cmd, result);
                    return messageParams.onsend?.(seq, cmd, data, result);
                }),
            onsendAccept:
                messageParams.onsendAccept &&
                ((
                    seq: number,
                    cmd: string,
                    data: MessageDataType,
                    waitCmd: string | undefined,
                    result: IRoomSendMessageResult,
                ) => {
                    this.reportWebsocketOnAck(cmd, result);
                    return messageParams.onsendAccept?.(seq, cmd, data, waitCmd, result);
                }),
        };
        this.connectionController?.sendMessage(message);
    }

    public cancelMessageByTaskId(taskId: number) {
        // http chunk 不支持 cancel
        if (this.connectMode === ConnectMode.BIG_COLLAB) return;
        this.connectionController?.cancelMessageBySeq(taskId);
    }

    /**
     * 初始化socket 私有方法调用实例时自动初始化
     * @param socketConfig 构造connectionController所需参数
     */
    public init(socketConfig: ICreateControllerParams) {
        initLogAndManager(console);
        this.socketConfig = socketConfig;
        this.connectionController = this.createConnectionController(socketConfig);
        this.initConnectionEvent(this.connectionController);
    }

    public reInit(socketConfig: ICreateControllerParams) {
        initLogAndManager(console);
        this.socketConfig = socketConfig;
        this.connectionController?.dispose();
        this.connectionController = this.createConnectionController(socketConfig);
        this.initConnectionEvent(this.connectionController);
    }

    public getSocketConfig() {
        return this.socketConfig;
    }

    public switchBigCollabMode = () => this.switchConnectMode(ConnectMode.BIG_COLLAB);

    private switchConnectMode(mode: ConnectMode) {
        this.connectionController?.reconnect(undefined, mode);
        this.connectMode = mode;
    }

    /**
     * 创建ConnectionController
     * 业务只和ConnectionController通信
     */
    private createConnectionController(socketOps: ICreateControllerParams) {
        const { serverVars, userId, uid, nowUserIndex, headerGeneralPacket } = socketOps;
        // Server拉取下来的默认为0不正确，强制修正
        const options: IConnectionControllerOptions = {
            serverVars,
            uuId: generateShortUUID(),
            userid: userId,
            module: DOCUMENT_TYPE.SLIDE,
            nowUserIndex,
            uid,
            config: {
                // 企微环境isUseAllCookie为true
                isUseAllCookie: isCorpWeChat(),
                headerGeneralPacket,
                messagePriority: new Map([
                    [Command.CURSOR_MESSAGE, MessagePriority.LOW],
                    [Command.NEW_CHANGES, MessagePriority.HIGH],
                ]),
                // bigDataCommitConfig: {
                //     isSupportBigDataCommit: true,
                //     // 2M 以上数据转大数据通道
                //     minContentLength: 0.5 * 1024 * 1024,
                //     // 最大数据量限制在 20M 以下
                //     maxContentLength: 19.5 * 1024 * 1024,
                // },
            },
        };

        // 创建连接层
        const connectionController = new ConnectionController(options);
        // 主动更新版本
        connectionController.setVersion(serverVars?.rev);
        return connectionController;
    }

    private initConnectionEvent(connectionController: ConnectionController) {
        // 监听连接层回调事件
        connectionController?.onConnectionEvent((result: IConnectionCallBackResult) => {
            /**
             * onEnterOnline/onEnterOffline 是通过 CDN 探测网络的网络状态，不代表房间层状态
             * onRoomOpen/onRoomClose/onRoomShutDown 是房间层状态，与用 ws http chunk 无关
             * onRoomStateChange/onNetReconnectedFail 是 ws 状态变化
             * connectedStateChange 是 http chunk 状态变化，但感觉不用关注
             */
            const connectionCallBackEventMap = new Map([
                [ConnectionCallBackEvent.onRoomOpen, () => this.handleOnRoomOpen()],
                [
                    ConnectionCallBackEvent.onRoomClose,
                    () => this.handleOnRoomClose(result.data?.closeReason ?? { shutDown: false }),
                ],
                [ConnectionCallBackEvent.onNetReconnectedFail, () => this.onNetReconnectedFail()],
                [
                    ConnectionCallBackEvent.onRoomShutDown,
                    () => this.handleOnRoomShutDown(result.data?.closeReason ?? { shutDown: true }),
                ],
                // 切 http chunk 后只在初始化的时候通知
                [
                    ConnectionCallBackEvent.onRoomStateChange,
                    () =>
                        this.handleOnRoomStateChange(
                            result.data?.roomState?.roomState ?? '',
                            result.data?.roomState?.description ?? '',
                        ),
                ],
                [
                    ConnectionCallBackEvent.onReceiveMsgFromServer,
                    () => this.handleOnReceivMsgFromServer(result.data?.serverMsg),
                ],
            ]);
            connectionCallBackEventMap.get(result.event)?.();
        });
    }

    // eslint-disable-next-line complexity
    private handleMessageFromServer(type: string, data: IServerMessage) {
        this.event.emit(SocketEventTypes.ReceiveMessage, type, data);
        switch (type) {
            case ServerMsgType.NEW_CHANGES:
                return this.handleNewChanges(data);
            case ServerMsgType.USER_NEWINFO:
                return this.handleUserNewInfo(data);
            case ServerMsgType.USER_NEWINFOS:
                return this.handleUsersNewInfo(data);
            case ServerMsgType.USER_LEAVE:
                return this.handleUserLeave(data);
            case ServerMsgType.CURSOR_MESSAGE:
                return this.handleCursorMessage(data);
            case ServerMsgType.CURSOR_MESSAGES:
                return this.handleCursorMessages(data);
            case ServerMsgType.RIGHT_MESSAGE:
                return this.handleRightMessage(data);
            case ServerMsgType.TITLE_MESSAGE:
                return this.handleTitleMessage(data);
            case ServerMsgType.HANDLE_DELETE:
                return this.handleDeletePad();
            case ServerMsgType.COMM_NOTICE:
                // 目前 webOffice 用来还原文档
                return this.handleCommonNotice(data);
            // case ServerMsgType.CODEREV_NEW:
            //     // 据说是有新版本代码，没有引用
            //     return;
            case ServerMsgType.LAST_REVERSION:
                return this.handleLastRevision(data);
            case ServerMsgType.REVERT_MESSAGE:
                return this.handleRevertMessage(data);
            case ServerMsgType.ALL_LOCK_INFO:
                return this.handleLockMessage(data);
            case ServerMsgType.UNLOCK_INFO:
                return this.handleUnlockMessage(data);
            case ServerMsgType.COMMERCIALIZE_MESSAGE:
                return this.handleCommercialMsg(data);
            case ServerMsgType.MARK_MESSAGE:
                return this.handleWatermarkMessage(data);
            case ServerMsgType.PRESENT_INFO:
                return this.handleEditingPresentNotice(data);
            case ServerMsgType.COMMIT_UPLOAD:
                return this.handleCommitUpload(data);
            default:
                break;
        }
    }

    // 商业化消息，如隐身查看
    private handleCommercialMsg(data: IServerMessage) {
        const notice = data?.body?.notice as IServerMessageBodyNoticeBase & {
            type: string;
            payload: { anonymous_status: number; operator: string };
        };
        if ((notice?.type as string) === 'anonymous_status') {
            const { anonymous_status, operator } = notice.payload;
            this.event.emit(SocketEventTypes.AnonymousChange, {
                status: anonymous_status,
                operator,
            });
            return;
        }
    }

    private handleRoomStateChange(channelState: string, moreInfo: string) {
        this.logger.log(MODULE_NAME, `room state change to ${channelState}`);
        const channelStateRef = channelState.toUpperCase();
        if (channelStateRef === 'RECONNECTING_IN_CLOSED' && this.opened) {
            this.handleOffline(moreInfo);
        } else if (channelStateRef === 'OPENED' && !this.opened) {
            this.handleOnline(moreInfo);
        }
    }

    /**
     * 企微内指定错误码开启重新续票逻辑
     * @param errCode
     * @returns
     */
    private needRenewTicket(errCode: number) {
        return isCorpWeChat() && 60006 === errCode;
    }

    private handleSocketShutDown(reason: string, errCode: number) {
        if (reason === 'unload') {
            this.logger.info(MODULE_NAME, `socket is closed by unload : ${reason}`);
            return;
        }

        this.logger.error(MODULE_NAME, `socket is closed ${errCode}`);

        if (this.inRenewTicketFlow) {
            // 已经在续票重连流程中，证明是尝试续票之后再次shutdown，此时不应再重试; 重置标志位后往下触发 messageHandler 的事件
            this.inRenewTicketFlow = false;
        } else if (this.needRenewTicket(errCode)) {
            // 票据过期错误码，开始续票重连, 不触发事件
            this.logger.info(MODULE_NAME, 'Login Expired, start renew ticket then reopen socket');
            this.renewTicketAndReOpenSocket(reason, errCode);
            return;
        }

        const error = GetSlideAppErrorByCode(errCode) ?? SlideAppError.Unknown;
        this.reportWebsocketOnShutdown(errCode, reason);
        this.event.emit(SocketEventTypes.ShutDown, { reason, code: errCode, error });
    }

    private handleOnline(message = '') {
        this.logger.info(MODULE_NAME, `online${message}`);
        this.event.emit(SocketEventTypes.ChangeToOnline);
    }

    private handleOffline(message = '') {
        this.logger.info(MODULE_NAME, `offline${message}`);
        this.event.emit(SocketEventTypes.ChangeToOffline);
    }

    /**
     * 新数据变更
     * @param data
     */
    private handleNewChanges = (data: IServerMessage) => {
        data && this.event.emit(SocketEventTypes.NewChanges, data);
    };

    /**
     * 用户进入
     * @param data
     */
    private handleUserNewInfo = (data: IServerMessage) => {
        const userData = data?.body?.notice as IServerMessageBodyNoticeUserNewInfo;
        userData && this.event.emit(SocketEventTypes.UserEnter, userData);
    };

    /**
     * 批量用户进入
     * @param data
     */
    private handleUsersNewInfo = (data: IServerMessage) => {
        const usersDatas = data?.body?.notices as IServerMessageBodyNoticeUserNewInfo[];
        usersDatas?.forEach((userData) => this.event.emit(SocketEventTypes.UserEnter, userData));
    };

    /**
     * 用户离开
     * @param data
     */
    private handleUserLeave = (data: IServerMessage) => {
        const userData = data?.body?.notice as IServerMessageBodyNoticeUserLeave;
        userData && this.event.emit(SocketEventTypes.UserLeave, userData);
    };

    /**
     * 光标
     * @param data
     */
    private handleCursorMessage = (data: IServerMessage) => {
        const cursorData = data?.body?.notice as IServerMessageBodyNoticeCursor;
        cursorData && this.event.emit(SocketEventTypes.CursorMessage, cursorData);
    };

    private handleCursorMessages = (data: IServerMessage) => {
        const cursorDatas = data?.body?.notices as IServerMessageBodyNoticeCursor[];
        cursorDatas?.forEach((cursorData) => this.event.emit(SocketEventTypes.CursorMessage, cursorData));
    };

    /**
     * 权限
     * @param data
     */
    private handleRightMessage = (data: IServerMessage) => {
        const rightData = data?.body?.notice;
        rightData && this.event.emit(SocketEventTypes.RightMessage, rightData);
    };

    /**
     * 标题
     * @param data
     */
    private handleTitleMessage = (data: IServerMessage) => {
        const titleInfo = data.body.notice as IServerMessageBodyNoticeTitle;
        titleInfo && this.event.emit(SocketEventTypes.TitleMessage, titleInfo);
    };

    /**
     * 删除文档
     * @param data
     */
    private handleDeletePad = () => {
        this.event.emit(SocketEventTypes.DeleteDoc);
    };

    /**
     * 最新版本号
     * @param data
     */
    private handleLastRevision = (data: IServerMessage) => {
        const revisionInfo = data.body.notice;
        if (!revisionInfo) return;
        const { lastRev } = revisionInfo as any;
        this.logger.info(MODULE_NAME, `receive last revision: ${lastRev}`);
        this.event.emit(SocketEventTypes.LatestVersion, lastRev);
    };

    /**
     * 修订记录还原
     * @param data
     */
    private handleRevertMessage = (data: IServerMessage) => {
        data && this.event.emit(SocketEventTypes.RevertMessage, data);
    };

    /**
     * 收到协同加锁信息
     * @param data
     */
    private handleLockMessage = (data: IServerMessage) => {
        const lockInfo = data.body.notice;
        lockInfo && this.event.emit(SocketEventTypes.AllLockInfo, lockInfo);
    };

    /**
     * 收到协同解锁信息
     * @param data
     */
    private handleUnlockMessage = (data: IServerMessage) => {
        const unLockInfo = data.body.notice;
        unLockInfo && this.event.emit(SocketEventTypes.UnLockInfo, unLockInfo);
    };

    /**
     * 水印变更通知
     * @param data
     */
    private handleWatermarkMessage = (data: IServerMessage) => {
        const watermarkInfo = data.body.notice as { watermark?: string };
        watermarkInfo && this.event.emit(SocketEventTypes.WaterMarkChange, watermarkInfo);
    };

    /** 续票重连，若无法续票则使用 handleShutDown 兜底 */
    private async renewTicketAndReOpenSocket(reason: string, errCode: number) {
        const { renewService } = this.messageHandler;

        if (renewService?.renewTicket(location.href)) {
            try {
                // 无论续票请求的响应是什么, 都认为续票成功;
                await renewService.awaitLoginTicket(location.href);

                this.inRenewTicketFlow = true;
                this.reConnect();
                return;
            } catch (error) {
                this.logger.error('[rev] renew service failed');
            }
        }
        const error = GetSlideAppErrorByCode(errCode) ?? SlideAppError.Unknown;
        this.event.emit(SocketEventTypes.ShutDown, { reason, error, code: errCode });
    }

    /**
     * 编辑态下的远程演示(编辑态跟随) 通知
     * @param data
     */
    private handleEditingPresentNotice = (data: IServerMessage) => {
        const presentInfo = data.body.notice as IServerMessageBodyNoticePresentInfo;
        presentInfo && this.event.emit(SocketEventTypes.PresentInfo, presentInfo);
    };

    private handleCommonNotice = (data: any) => {
        const notice = data?.body?.notice;
        if (notice?.type === 'rollback') {
            this.event.emit(SocketEventTypes.WebOfficeRevert);
        }
    };

    private handleOnRoomClose(reason: IRoomCloseReason): void {
        this.logger.info(MODULE_NAME, 'socket is closed ', reason);
        if (reason.errCode === RoomWebsocketErrorCode.SOCKET_SWITCH_BIG_COLLAB) {
            this.switchConnectMode(ConnectMode.BIG_COLLAB);
            return;
        }
        if (reason.errCode === RoomErrorCode.BIG_COLLAB_CONNECT_TIMEOUT) {
            this.switchConnectMode(ConnectMode.WEBSOCKET_FORCED);
            return;
        }
        this.handleOffline();
    }

    private handleOnRoomOpen(): void {
        this.logger.info(MODULE_NAME, 'socket is opened');

        // 续票后重建 socket 成功，此时向 renewService 回调成功
        if (this.inRenewTicketFlow) {
            this.inRenewTicketFlow = false;
            this.messageHandler.renewService?.renewSuccess(location.href);
        }

        this.monitor4OpenSocketSucceed();
        this.handleOnline();
    }

    private onNetReconnectedFail(): void {
        this.logger.info(MODULE_NAME, 'socket reconnect fail');
    }

    private handleOnRoomShutDown(reason: IRoomCloseReason): void {
        if (reason.errCode === RoomWebsocketErrorCode.SOCKET_SWITCH_BIG_COLLAB) {
            this.switchConnectMode(ConnectMode.BIG_COLLAB);
            return;
        }
        if (reason.errCode === RoomErrorCode.BIG_COLLAB_CONNECT_TIMEOUT) {
            this.switchConnectMode(ConnectMode.WEBSOCKET_FORCED);
            return;
        }
        this.handleSocketShutDown(reason.reason || 'unknown', reason.errCode || 0);
    }

    private handleOnRoomStateChange(roomState: string, moreInfo: string): void {
        this.handleRoomStateChange(roomState, moreInfo);
    }

    private handleOnReceivMsgFromServer(connectionCallBackServerMsg?: IConnectionCallBackServerMsg): void {
        if (!connectionCallBackServerMsg) {
            return;
        }
        const { type, data } = connectionCallBackServerMsg;
        if (!(data instanceof Array)) {
            this.logger.error(MODULE_NAME, 'handleOnReceivMsgFromServer - data is not array');
            return;
        }
        data.forEach((serverMsg) => this.handleMessageFromServer(type, serverMsg));
    }

    /**
     * 私有化专属，上传新版本后会触发该事件
     */
    private handleCommitUpload(data: IServerMessage) {
        data && this.event.emit(SocketEventTypes.CommitUpload, data);
    }

    /**
     * socket 接口 onSend 上报
     * 由于底层封装的 007 上报不支持多维度，因此
     * 1. 将 cmd 作为属性名多报一条
     * 2. 将 retcode / msg 等信息包到 errMsg 中
     */
    private reportWebsocketOnSend(cmd: string, result: IRoomSendMessageResult) {
        const websocketSend = 'sdk_websocket_send';
        const attrId = `${websocketSend}/${cmd}`;
        if (result?.errCode === 0) {
            this.logger.monitor?.reportStatus?.(websocketSend, MonitorStatus.SUCCESS);
            this.logger.monitor?.reportStatus?.(attrId, MonitorStatus.SUCCESS);
            return;
        }

        this.logger.monitor?.reportStatus?.(websocketSend, MonitorStatus.ERROR);
        this.logger.monitor?.reportStatus?.(attrId, MonitorStatus.ERROR, `${result?.errCode}`);

        this.logger.aegis?.report?.(new Error(`errorCode:${result?.errCode}`));
        this.logger.tdrec?.(result?.errCode, 'websocket');
    }

    /**
     * socket 接口 onSendAck 上报, @see reportWebsocketOnSend
     * @param cmd
     * @param result
     * @param errorMsg
     * @returns
     */
    private reportWebsocketOnAck(cmd: string, result: IRoomSendMessageResult) {
        const websocketSend = 'sdk_websocket_send_ack';
        const attrId = `${websocketSend}/${cmd}`;
        if (result?.errCode === 0) {
            this.logger.monitor?.reportStatus?.(websocketSend, MonitorStatus.SUCCESS);
            this.logger.monitor?.reportStatus?.(attrId, MonitorStatus.SUCCESS);
            return;
        }

        this.logger.monitor?.reportStatus?.(websocketSend, MonitorStatus.ERROR);
        this.logger.monitor?.reportStatus?.(attrId, MonitorStatus.ERROR, `${result?.errCode}`);

        this.logger.aegis?.report?.(new Error(`errorCode:${result?.errCode}`));
        this.logger.tdrec?.(result?.errCode, 'websocket');
    }

    /**
     * socket 异常终止上报
     * @param errCode
     * @param errorMsg
     */
    private reportWebsocketOnShutdown(errCode: number, errorMsg: string) {
        this.logger.monitor?.reportStatus?.('sdk_websocket_shutdown', MonitorStatus.ERROR, `${errCode}_${errorMsg}`);
        this.logger.aegis?.report?.(new Error(`${errorMsg}, errorCode:${errCode}`));
        this.logger.tdrec?.(errCode, 'websocket');
    }

    private monitor4OpenSocketTimeout() {
        let waitTime = SOCKET_ESTABLISH_TIME_OUT;
        let curTime = performance.now();
        const visibilitychangeCb = () => {
            if (
                // 如果 socket 已经 shutdown 掉了，说明前面已经出错并处理了，这里就不认为是 socket 连接有问题了。
                // 如果不这么处理的话，对于打开页面就因为出错 shutdown 的 case，会误报成 socket 有问题
                this.isShutDown ||
                // 房间超载且不是创建者本人
                (this.clientVarProvider.isOverConNum && !this.clientVarProvider.isCreator) ||
                // 云编的非作者需要屏蔽协同功能
                (this.clientVarProvider.isDriveFile && !this.clientVarProvider.isCreator)
            ) {
                clearTimeout(this.reportEstTimer);
                window.removeEventListener('visibilitychange', visibilitychangeCb);
                return;
            }

            if (document.visibilityState === 'visible') {
                curTime = performance.now();
                this.reportEstTimer = setTimeout(() => {
                    // 考虑到主线程繁忙，定时器回调比 socket 连接成功的回调先执行
                    if (this.estMoreThan90 && !this.opened && !this.isShutDown) {
                        this.reportOpenSocketMoreThan90S();
                    }
                    window.removeEventListener('visibilitychange', visibilitychangeCb);
                }, waitTime);
            } else {
                waitTime = Math.max(waitTime - (performance.now() - curTime), 0);
                clearTimeout(this.reportEstTimer);
            }
        };
        visibilitychangeCb();
        window.addEventListener('visibilitychange', visibilitychangeCb);
    }

    /** 监控socket 成功 */
    private monitor4OpenSocketSucceed() {
        this.estMoreThan90 = false;
    }

    private reportOpenSocketMoreThan90S() {
        network.isOnline &&
            this.logger.monitor?.reportStatus?.('90秒以内未和后台建立编辑通道', MonitorStatus.ERROR, '', [
                this.socketConfig.serverVars.globalPadId ?? '',
                this.connectMode,
            ]);
    }
}

type IRenewService = any;

export interface ISocketMessageHandler {
    // FIXME: 从外部透传进来的 service， 挂载在 SlideApp 上，实际不属于 ISocketMessageHandler
    renewService?: IRenewService;
}

export interface ICreateControllerParams {
    serverVars: IConnectionControllerOptions['serverVars'];
    userId: string;
    uid: string;
    nowUserIndex?: string;
    headerGeneralPacket: {
        rel_rev?: number;
        right_tag: number;
        platform: number;
    };
}
